import { PRO014_STORE_MODE } from "@/@types/campaign";
import type {
  IPro014StoreWindow,
  IUpdatePro014StoresApplied,
  IUpdatePro014StoresBody,
  IUpdatePro014StoresLegacyApplied,
  IUpdatePro014StoresNoop,
  IUpdatePro014StoresQueued,
  IUpdatePro014StoresResponse,
  Pro014StoreMode,
  Pro014StoreScope,
  Pro014StoresUpdateResult,
  StoreSelectorValue,
} from "@/@types/campaign";

import { ALL_STORES_CODE, PRO014_STORES_TEXTS } from "../components/edit-campaign/constants";
import type { Pro014StoresPreview } from "../types";
import { MESSAGES } from "./messages";

const ascending = (first: number, second: number): number => first - second;

const sortedCodes = (codes: number[]): number[] => [...codes].sort(ascending);

const storeRows = (windows: IPro014StoreWindow[] | undefined): IPro014StoreWindow[] =>
  (windows ?? []).filter((window: IPro014StoreWindow) => window.storeCode !== ALL_STORES_CODE);

export const sortedStoreWindows = (
  windows: IPro014StoreWindow[] | undefined,
): IPro014StoreWindow[] =>
  [...storeRows(windows)].sort((first, second) => ascending(first.storeCode, second.storeCode));

export const activeStoreCodes = (windows: IPro014StoreWindow[] | undefined): number[] =>
  sortedCodes(
    storeRows(windows)
      .filter((window: IPro014StoreWindow) => window.active)
      .map((window: IPro014StoreWindow) => window.storeCode),
  );

const previewToAll = (currentlyAll: boolean): Pro014StoresPreview => ({
  switchesToAll: !currentlyAll,
  switchesToSpecific: false,
  chosen: [],
  toAdd: [],
  toReopen: [],
  toEnd: [],
  hasChanges: !currentlyAll,
});

const previewToSpecific = (stores: number[]): Pro014StoresPreview => ({
  switchesToAll: false,
  switchesToSpecific: true,
  chosen: sortedCodes(stores),
  toAdd: [],
  toReopen: [],
  toEnd: [],
  hasChanges: true,
});

export const buildPro014StoresPreview = (
  storeMode: Pro014StoreMode,
  windows: IPro014StoreWindow[],
  selection: StoreSelectorValue,
): Pro014StoresPreview => {
  const currentlyAll: boolean = storeMode === PRO014_STORE_MODE.ALL;
  if (selection.allStores) return previewToAll(currentlyAll);
  if (currentlyAll) return previewToSpecific(selection.stores);

  const rows: IPro014StoreWindow[] = storeRows(windows);
  const known: Set<number> = new Set(rows.map((row: IPro014StoreWindow) => row.storeCode));
  const ended: Set<number> = new Set(
    rows.filter((row: IPro014StoreWindow) => !row.active).map((row) => row.storeCode),
  );
  const selected: Set<number> = new Set(selection.stores);
  const toAdd: number[] = sortedCodes(selection.stores.filter((code: number) => !known.has(code)));
  const toReopen: number[] = sortedCodes(selection.stores.filter((code: number) => ended.has(code)));
  const toEnd: number[] = sortedCodes(
    rows
      .filter((row: IPro014StoreWindow) => row.active && !selected.has(row.storeCode))
      .map((row: IPro014StoreWindow) => row.storeCode),
  );
  const touchedStores: number = toAdd.length + toReopen.length + toEnd.length;

  return {
    switchesToAll: false,
    switchesToSpecific: false,
    chosen: [],
    toAdd,
    toReopen,
    toEnd,
    hasChanges: touchedStores > 0,
  };
};

export const normalizeStoreSelection = (
  selection: StoreSelectorValue,
  allStoreCodes: number[],
): StoreSelectorValue => {
  if (selection.allStores || allStoreCodes.length === 0) return selection;
  const selected: Set<number> = new Set(selection.stores);
  const coversAll: boolean = allStoreCodes.every((code: number) => selected.has(code));
  return coversAll ? { allStores: true, stores: [] } : selection;
};

export const initialPro014StoresSelection = (
  detail: Pro014StoreScope,
): StoreSelectorValue => ({
  allStores: detail.storeMode === PRO014_STORE_MODE.ALL,
  stores: activeStoreCodes(detail.storeWindows),
});

export const buildPro014StoresPayload = (
  selection: StoreSelectorValue,
): IUpdatePro014StoresBody => {
  if (selection.allStores) return { allStores: true };
  return { allStores: false, stores: sortedCodes(selection.stores) };
};

export const isPro014StoresSelectionValid = (selection: StoreSelectorValue): boolean =>
  selection.allStores || selection.stores.length > 0;

const countOf = (storeCodes: number[] | undefined): number => storeCodes?.length ?? 0;

const isNoop = (data: Pro014StoresUpdateResult): data is IUpdatePro014StoresNoop =>
  "noop" in data && data.noop === true;

const isQueued = (data: Pro014StoresUpdateResult): data is IUpdatePro014StoresQueued =>
  "queued" in data && data.queued === true;

export const isQueuedStoresUpdate = (response: IUpdatePro014StoresResponse): boolean =>
  Boolean(response?.data) && isQueued(response.data);

const isCentral = (data: Pro014StoresUpdateResult): data is IUpdatePro014StoresApplied =>
  "storeMode" in data;

const describeCentral = (data: IUpdatePro014StoresApplied): string => {
  if (data.storeMode === PRO014_STORE_MODE.ALL) return PRO014_STORES_TEXTS.SWITCHED_TO_ALL;
  return PRO014_STORES_TEXTS.UPDATED(
    countOf(data.added),
    countOf(data.reopened),
    countOf(data.reset),
    countOf(data.ended),
  );
};

const describeLegacy = (data: IUpdatePro014StoresLegacyApplied): string =>
  PRO014_STORES_TEXTS.LEGACY_UPDATED(countOf(data.added), countOf(data.removed));

export const describePro014StoresUpdate = (response: IUpdatePro014StoresResponse): string => {
  const data: Pro014StoresUpdateResult | undefined = response?.data;
  if (!data) return MESSAGES.UPDATE_STORES.SUCCESS.description;
  if (isNoop(data)) return PRO014_STORES_TEXTS.UNCHANGED;
  if (isQueued(data)) return PRO014_STORES_TEXTS.QUEUED;
  if (isCentral(data)) return describeCentral(data);
  return describeLegacy(data);
};

export const pro014StoresVersion = (
  detail: Pro014StoreScope,
): string =>
  [
    detail.storeMode,
    ...storeRows(detail.storeWindows).map(
      (window: IPro014StoreWindow) =>
        `${window.storeCode}:${window.active}:${window.startDate}:${window.endDate}`,
    ),
  ].join("|");
